import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function MicrosoftAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithMicrosoft } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    // Extract code from URL query params (Microsoft uses query, not hash)
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || 'Authentication was cancelled or failed');
      toast.error('Microsoft authentication failed');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (!code) {
      setError('No authorization code received. Please try again.');
      toast.error('Authentication failed. No code received.');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    // Exchange code for user data and JWT token
    const handleCallback = async () => {
      try {
        const result = await loginWithMicrosoft(code);

        if (result.needs_role_selection || result.user_type === 'pending') {
          navigate('/select-role');
        } else {
          toast.success(`Welcome${result.name ? ', ' + result.name : ''}!`);
          const isAdminUser = result.user_type === 'admin' || result.user_type === 'super_admin' || result.user_type === 'sub_admin' || result.user_type === 'school_admin';
          navigate(isAdminUser ? '/admin' : '/dashboard');
        }
      } catch (err) {
        console.error('Microsoft OAuth callback error:', err);
        setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
        toast.error('Microsoft authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [searchParams, loginWithMicrosoft, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <span className="text-2xl">❌</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Completing Microsoft Sign In...</h2>
        <p className="text-gray-600">Please wait while we authenticate your account.</p>
      </div>
    </div>
  );
}
